/**
 * @fileoverview Usage Meter Component
 * 
 * Displays monthly test-run usage and free tier percentage in the top-right
 * of the dashboard. Shows current usage against plan limits.
 * 
 * @author Claude Code
 * @since 2024-01-01
 */

import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, 
  Zap, 
  ArrowUpRight,
  AlertTriangle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '@/lib/analytics';
import { useAuth } from '@/lib/auth/useAuth';
import { getCurrentUsageSummary } from '@/api/usage';

interface UsageData {
  currentMonthRuns: number;
  planLimit: number;
  planName: string;
  usagePercentage: number;
  daysRemaining: number;
}

/**
 * Props for the UsageMeter component
 */
interface UsageMeterProps {
  /** Optional CSS class name for custom styling */
  className?: string;
  /** Whether to display in compact mode (for header) or full-width mode (for bottom) */
  variant?: 'compact' | 'full-width';
}

/**
 * Usage Meter Component
 * 
 * Shows current month's test-run usage against plan limits with:
 * - Current usage vs plan limit
 * - Usage percentage with progress bar
 * - Plan name and upgrade prompts
 * - Days remaining in current billing cycle
 * 
 * @param props - Component props
 * @returns Rendered usage meter component
 */
const UsageMeter: React.FC<UsageMeterProps> = ({ className, variant = 'compact' }) => {
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  /**
   * Fetch usage data from API
   */
  useEffect(() => {
    const fetchUsageData = async () => {
      if (!isAuthenticated || !user) {
        setIsLoading(false);
        return;
      }

      try {
        // Fetch from real API
        const response = await getCurrentUsageSummary();
        
        if (response.success && response.data) {
          setUsageData(response.data);
        }
      } catch (error) {
        console.error('Error fetching usage data:', error);
        // Fallback to basic free tier data
        setUsageData({
          currentMonthRuns: 0,
          planLimit: 100,
          planName: 'Free',
          usagePercentage: 0,
          daysRemaining: 30
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsageData();
  }, [isAuthenticated, user]);

  /**
   * Handle upgrade button click
   */
  const handleUpgradeClick = () => {
    navigate('/settings/billing');
    trackEvent('usage_meter_upgrade_clicked', {
      current_usage: usageData?.usagePercentage || 0,
      plan: usageData?.planName || 'unknown'
    });
  };

  /**
   * Get usage status styling based on percentage
   */
  const getUsageStatus = (percentage: number | undefined) => {
    const safePercentage = percentage ?? 0;
    if (safePercentage >= 90) return { color: 'text-red-600', bgColor: 'bg-red-500', alert: true };
    if (safePercentage >= 80) return { color: 'text-yellow-600', bgColor: 'bg-yellow-500', alert: true };
    if (safePercentage >= 60) return { color: 'text-blue-600', bgColor: 'bg-blue-500', alert: false };
    return { color: 'text-green-600', bgColor: 'bg-green-500', alert: false };
  };

  /**
   * Authentication Guard
   */
  if (!isAuthenticated || !user) {
    return null;
  }

  if (isLoading) {
    const cardClasses = variant === 'full-width' ? 'w-full' : 'w-64';
    return (
      <Card className={`${cardClasses} ${className}`}>
        <CardContent className="p-4">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-2 bg-gray-200 rounded mb-3"></div>
            <div className="h-3 bg-gray-200 rounded w-16"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!usageData) {
    return null;
  }

  const status = getUsageStatus(usageData.usagePercentage);
  const cardClasses = variant === 'full-width' ? 'w-full' : 'w-64';

  if (variant === 'full-width') {
    return (
      <Card className={`${cardClasses} ${className}`}>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
            {/* Header Section */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Zap className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-base">Monthly Usage</h3>
                <p className="text-sm text-muted-foreground">
                  {usageData.planName === 'Starter' ? 'Starter · 100 free runs/mo' : 
                   usageData.planName === 'Pro' ? 'Pro · 1,000 runs/mo' :
                   usageData.planName === 'Enterprise' ? 'Enterprise · 5,000 runs/mo' :
                   usageData.planName ? `${usageData.planName} Plan` : 'Free Plan'}
                </p>
              </div>
            </div>

            {/* Usage Stats */}
            <div className="text-center md:text-left">
              <div className="text-2xl font-bold">
                {(usageData.currentMonthRuns ?? 0).toLocaleString()}
              </div>
              <p className="text-sm text-muted-foreground">
                of {(usageData.planLimit ?? 0).toLocaleString()} runs
              </p>
            </div>

            {/* Progress Section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className={status.color}>
                  {(usageData.usagePercentage ?? 0)}% used
                </span>
                <span className="text-muted-foreground">
                  {(usageData.daysRemaining ?? 0)} days left
                </span>
              </div>
              <Progress 
                value={usageData.usagePercentage ?? 0} 
                className="h-3"
              />
              {status.alert && (
                <div className="flex items-center gap-2 text-xs text-yellow-600">
                  <AlertTriangle className="h-3 w-3" />
                  <span>
                    {(usageData.usagePercentage ?? 0) >= 90 ? 'Almost at limit!' : 'Approaching limit'}
                  </span>
                </div>
              )}
            </div>

            {/* Actions Section */}
            <div className="flex gap-2 justify-end">
              {(usageData.planName === 'Starter' || usageData.planName === 'Free') && (usageData.usagePercentage ?? 0) > 50 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleUpgradeClick}
                  className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20 hover:border-primary/40"
                >
                  <TrendingUp className="mr-2 h-4 w-4" />
                  {(usageData.usagePercentage ?? 0) >= 80 ? 'Upgrade Now' : 'Upgrade Plan'}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigate('/usage');
                  trackEvent('usage_meter_details_clicked');
                }}
              >
                View Details
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Compact variant (original design)
  return (
    <Card className={`${cardClasses} ${className}`}>
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Usage</span>
            </div>
            <Badge 
              variant={usageData.planName === 'Starter' || usageData.planName === 'Free' ? 'secondary' : 'default'}
              className="text-xs"
            >
              {usageData.planName === 'Starter' ? 'Starter' : 
               usageData.planName === 'Pro' ? 'Pro' :
               usageData.planName === 'Enterprise' ? 'Enterprise' :
               usageData.planName || 'Free'}
            </Badge>
          </div>

          {/* Usage Numbers */}
          <div className="space-y-1">
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-bold">
                {(usageData.currentMonthRuns ?? 0).toLocaleString()}
              </span>
              <span className="text-sm text-muted-foreground">
                / {(usageData.planLimit ?? 0).toLocaleString()} runs
              </span>
            </div>
            
            {/* Progress Bar */}
            <Progress 
              value={usageData.usagePercentage ?? 0} 
              className="h-2"
            />
            
            <div className="flex items-center justify-between text-xs">
              <span className={status.color}>
                {(usageData.usagePercentage ?? 0)}% used
              </span>
              <span className="text-muted-foreground">
                {(usageData.daysRemaining ?? 0)} days left
              </span>
            </div>
          </div>

          {/* Alert or Upgrade Prompt */}
          {status.alert && (
            <div className="flex items-center gap-2 p-2 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-md">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <span className="text-xs text-yellow-800 dark:text-yellow-200">
                {(usageData.usagePercentage ?? 0) >= 90 ? 'Almost at limit!' : 'Approaching limit'}
              </span>
            </div>
          )}

          {/* Upgrade Button for Starter/Free Tier */}
          {(usageData.planName === 'Starter' || usageData.planName === 'Free') && (usageData.usagePercentage ?? 0) > 50 && (
            <Button
              size="sm"
              variant="outline"
              className="w-full text-xs"
              onClick={handleUpgradeClick}
            >
              <TrendingUp className="mr-1 h-3 w-3" />
              Upgrade Plan
              <ArrowUpRight className="ml-1 h-3 w-3" />
            </Button>
          )}

          {/* View Details Link */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs h-6"
            onClick={() => {
              navigate('/usage');
              trackEvent('usage_meter_details_clicked');
            }}
          >
            View Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default UsageMeter;